import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  Person as PersonIcon,
  Description as DocumentIcon,
  AutoAwesome as AiIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import api from '../../../api/axios';
import RichTextEditor from '../../RichTextEditor';
import RichTextDisplay from '../../RichTextDisplay';
import { EVENT_TYPE_COLORS, getEventIcon } from '../constants/eventConfig';
import { sanitizeEmailHtml } from '../utils/emailSanitization';
import { TimelineEvent } from '../types/timeline.types';
import { STATUS_COLORS } from '../../../utils/taskColors';

// Helper function to get status display text
const getStatusDisplay = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    pending: 'Pending',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
    blocked: 'Blocked',
  };
  return statusMap[status] || status;
};

// Helper function to get event category label
const getEventCategoryLabel = (eventType: string, EMAIL_EVENTS: string[], TASK_EVENTS: string[], DOCUMENT_EVENTS: string[], ENTITY_EVENTS: string[], DEFENDANT_EVENTS: string[], TEAM_EVENTS: string[]): string => {
  if (eventType === 'log_entry') return 'Case Logs';
  if (TASK_EVENTS.includes(eventType)) return 'Tasks';
  if (EMAIL_EVENTS.includes(eventType)) return 'Emails';
  if (DOCUMENT_EVENTS.includes(eventType)) return 'Documents';
  if (ENTITY_EVENTS.includes(eventType)) return 'Entities';
  if (DEFENDANT_EVENTS.includes(eventType)) return 'Defendants';
  if (TEAM_EVENTS.includes(eventType)) return 'Team Members';
  if (eventType === 'case_created') return 'Case Created';
  if (eventType === 'status_changed') return 'Status Changed';
  return 'Other';
};

// Helper function to format metadata
const formatMetadata = (event: TimelineEvent) => {
  const parts: string[] = [];

  if (event.metadata?.name) {
    parts.push(event.metadata.identifier
      ? `${event.metadata.name} (${event.metadata.identifier})`
      : event.metadata.name
    );
  }

  if (event.metadata?.entity_count) {
    parts.push(`${event.metadata.entity_count} entities`);
  }

  if (event.metadata?.defendant_count) {
    parts.push(`${event.metadata.defendant_count} defendants`);
  }

  if (event.metadata?.filename) {
    parts.push(event.metadata.filename);
  }

  if (event.metadata?.old_status && event.metadata?.new_status) {
    parts.push(`${event.metadata.old_status} → ${event.metadata.new_status}`);
  }

  return parts.join(' • ');
};

// Props interface
export interface StandardEventCardProps {
  event: TimelineEvent;
  isExpanded: boolean;
  isEditing: boolean;
  isNewlyCreated: boolean;
  isLastEvent: boolean;
  caseId: string;
  headers: any;

  // Email-related props
  email: {
    editingEmailTitleId: string | null;
    generatingEmailTitle: boolean;
    editEmailTitle: string;
    setEditEmailTitle: (title: string) => void;
    handleEditEmailTitle: (event: TimelineEvent) => void;
    handleSaveEmailTitle: (eventId: string) => void;
    handleCancelEmailTitleEdit: () => void;
    handleGenerateEmailTitle: (eventId: string) => void;
    handleRemoveTag: (eventId: string, tagId: string) => void;
    handleOpenTagDialog: (eventId: string) => void;
    handleOpenSummaryPopover: (e: React.MouseEvent<HTMLElement>, eventId: string) => void;
  };

  // Event type constants
  EMAIL_EVENTS: string[];
  TASK_EVENTS: string[];
  DOCUMENT_EVENTS: string[];
  ENTITY_EVENTS: string[];
  DEFENDANT_EVENTS: string[];
  TEAM_EVENTS: string[];

  // Edit state for logs
  editEventDate: string;
  setEditEventDate: (date: string) => void;
  editLogContent: string;
  setEditLogContent: (content: string) => void;
  editLogFiles: File[];
  setEditLogFiles: (files: File[]) => void;
  editLogExistingAttachments: any[];
  setEditLogExistingAttachments: (attachments: any[]) => void;
  editLogAttachmentsToRemove: string[];
  setEditLogAttachmentsToRemove: (ids: string[]) => void;

  // Edit state for tasks
  editTaskTitle: string;
  setEditTaskTitle: (title: string) => void;
  editTaskDescription: string;
  setEditTaskDescription: (description: string) => void;
  editTaskStatus: string;
  setEditTaskStatus: (status: string) => void;
  editTaskPriority: string;
  setEditTaskPriority: (priority: string) => void;
  editTaskAssignedTo: string;
  setEditTaskAssignedTo: (userId: string) => void;
  editTaskDueDate: string;
  setEditTaskDueDate: (date: string) => void;
  editTaskFiles: File[];
  setEditTaskFiles: (files: File[]) => void;
  editTaskExistingAttachments: any[];
  setEditTaskExistingAttachments: (attachments: any[]) => void;
  editTaskAttachmentsToRemove: string[];
  setEditTaskAttachmentsToRemove: (ids: string[]) => void;

  // Available users for task assignment
  availableUsers: any[];

  // Event handlers
  toggleEventExpanded: (eventId: string) => void;
  handleEditLogEntry: (event: TimelineEvent) => void;
  handleSaveLogEntry: (event: TimelineEvent) => void;
  handleEditTask: (event: TimelineEvent) => void;
  handleSaveTask: (event: TimelineEvent) => void;
  handleEditEvent: (event: TimelineEvent) => void;
  handleSaveEventDate: (eventId: string) => void;
  handleCancelEdit: () => void;
  handleDeleteClick: (event: TimelineEvent) => void;
}

export const StandardEventCard: React.FC<StandardEventCardProps> = (props) => {
  // Destructure all props
  const {
    event,
    isExpanded,
    isEditing,
    isNewlyCreated,
    isLastEvent,
    caseId,
    headers,
    email,
    EMAIL_EVENTS,
    TASK_EVENTS,
    DOCUMENT_EVENTS,
    ENTITY_EVENTS,
    DEFENDANT_EVENTS,
    TEAM_EVENTS,
    editEventDate,
    setEditEventDate,
    editLogContent,
    setEditLogContent,
    editLogFiles,
    setEditLogFiles,
    editLogExistingAttachments,
    setEditLogExistingAttachments,
    editLogAttachmentsToRemove,
    setEditLogAttachmentsToRemove,
    editTaskTitle,
    setEditTaskTitle,
    editTaskDescription,
    setEditTaskDescription,
    editTaskStatus,
    setEditTaskStatus,
    editTaskPriority,
    setEditTaskPriority,
    editTaskAssignedTo,
    setEditTaskAssignedTo,
    editTaskDueDate,
    setEditTaskDueDate,
    editTaskFiles,
    setEditTaskFiles,
    editTaskExistingAttachments,
    setEditTaskExistingAttachments,
    editTaskAttachmentsToRemove,
    setEditTaskAttachmentsToRemove,
    availableUsers,
    toggleEventExpanded,
    handleEditLogEntry,
    handleSaveLogEntry,
    handleEditTask,
    handleSaveTask,
    handleEditEvent,
    handleSaveEventDate,
    handleCancelEdit,
    handleDeleteClick,
  } = props;

  // Local state for email details expansion
  const [expandedEmailDetails, setExpandedEmailDetails] = useState<Set<string>>(new Set());

  return (
    <Box
      id={`event-${event.id}`}
      sx={{
        display: 'flex',
        position: 'relative',
        ...(isNewlyCreated && {
          animation: 'slideInFromTop 0.5s ease-out, highlightFade 2s ease-in-out',
          '@keyframes slideInFromTop': {
            '0%': {
              transform: 'translateY(-20px)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1,
            },
          },
          '@keyframes highlightFade': {
            '0%': {
              '& .event-card': {
                backgroundColor: 'primary.light',
                boxShadow: 3,
              }
            },
            '50%': {
              '& .event-card': {
                backgroundColor: 'primary.light',
                boxShadow: 3,
              }
            },
            '100%': {
              '& .event-card': {
                backgroundColor: event.is_important ? 'warning.50' : 'background.paper',
                boxShadow: 0,
              }
            },
          },
        }),
      }}
    >
      {/* Left: Date/Time Column */}
      <Box sx={{ width: 120, flexShrink: 0, pr: 2, pt: 1.5 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 600,
            color: 'text.secondary',
            fontSize: '0.75rem',
            lineHeight: 1.4,
          }}
        >
          {new Date(event.event_date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            fontSize: '0.7rem',
          }}
        >
          {new Date(event.event_date).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
          })}
        </Typography>
      </Box>

      {/* Timeline Line & Dot */}
      <Box sx={{ width: 40, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1 }}>
        {/* Dot/Marker */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: EVENT_TYPE_COLORS[event.event_type] || '#757575',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            zIndex: 2,
            boxShadow: `0 0 0 4px white, 0 0 0 5px ${EVENT_TYPE_COLORS[event.event_type] || '#757575'}`,
          }}
        >
          {getEventIcon(event.event_type, 'small')}
        </Box>
        {/* Vertical line extending down (hidden on last event) */}
        {!isLastEvent && (
          <Box
            sx={{
              width: 2,
              flex: 1,
              backgroundColor: 'divider',
              mt: -0.5,
            }}
          />
        )}
      </Box>

      {/* Right: Content Card */}
      <Box sx={{ flex: 1, minWidth: 0, pb: 3 }}>
        <Card
          className="event-card"
          sx={{
            border: '1px solid',
            borderColor: event.is_important ? 'warning.main' : 'divider',
            borderRadius: 2,
            backgroundColor: event.is_important ? 'warning.50' : 'background.paper',
            '&:hover': {
              boxShadow: 2,
            },
            transition: 'box-shadow 0.2s',
            position: 'relative',
          }}
        >
          {/* Collapsed header - always visible */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              cursor: 'pointer',
            }}
            onClick={() => !isEditing && toggleEventExpanded(event.id)}
          >

        {/* Title */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
            {/* Show editing field for email title if in edit mode */}
            {email.editingEmailTitleId === event.id ? (
              <>
                {email.generatingEmailTitle ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      Generating title...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <TextField
                      size="small"
                      value={email.editEmailTitle}
                      onChange={(e) => email.setEditEmailTitle(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          email.handleSaveEmailTitle(event.id);
                        }
                      }}
                      sx={{
                        width: '75%',
                        '& .MuiInputBase-root': {
                          fontSize: '0.875rem',
                          height: 32,
                        },
                      }}
                      autoFocus
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                email.handleGenerateEmailTitle(event.id);
                              }}
                              edge="end"
                              sx={{
                                padding: '4px',
                                color: 'primary.main',
                                '&:hover': {
                                  backgroundColor: 'primary.light',
                                },
                              }}
                              title="Generate title with Eliot"
                            >
                              <AiIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        email.handleSaveEmailTitle(event.id);
                      }}
                      color="primary"
                      sx={{ padding: '4px' }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        email.handleCancelEmailTitleEdit();
                      }}
                      sx={{ padding: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </>
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {event.custom_title || event.title}
                  {/* Show attachment count inline for log entries */}
                  {event.event_type === 'log_entry' && event.metadata?.has_attachments && event.metadata?.attachment_count > 0 && (
                    <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                      {' '}({event.metadata.attachment_count} attachment{event.metadata.attachment_count > 1 ? 's' : ''})
                    </Typography>
                  )}
                  {/* Show attachment count inline for tasks */}
                  {event.event_type.includes('task') && event.metadata?.has_attachments && event.metadata?.attachment_count > 0 && (
                    <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                      {' '}({event.metadata.attachment_count > 1 ? 's' : ''})
                    </Typography>
                  )}
                </Typography>

                {/* Edit Title button for email events */}
                {EMAIL_EVENTS.includes(event.event_type) && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      email.handleEditEmailTitle(event);
                    }}
                    sx={{
                      color: 'text.secondary',
                      padding: '2px',
                      opacity: 0.6,
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: 'action.hover',
                      },
                    }}
                    title="Edit email title"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}

                {/* Email Tags - show inline for email events */}
                {EMAIL_EVENTS.includes(event.event_type) && event.metadata?.email?.tags && event.metadata.email.tags.length > 0 && (
                  <>
                    {event.metadata.email.tags.map((tag: any) => (
                      <Chip
                        key={tag.id}
                        label={tag.name}
                        size="small"
                        onDelete={(e) => {
                          e.stopPropagation();
                          email.handleRemoveTag(event.id, tag.id);
                        }}
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          backgroundColor: tag.color,
                          color: 'white',
                          ml: 0.5,
                          '& .MuiChip-deleteIcon': {
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': {
                              color: 'white',
                            },
                          },
                        }}
                      />
                    ))}
                  </>
                )}

                {/* Add Tag button for email events - show inline with title */}
                {EMAIL_EVENTS.includes(event.event_type) && (
                  <Chip
                    label="+ Tag"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      email.handleOpenTagDialog(event.id);
                    }}
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      cursor: 'pointer',
                      ml: 0.5,
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  />
                )}

                {/* Separator and Email Participant Chips - show after tags for email events */}
                {EMAIL_EVENTS.includes(event.event_type) && event.metadata?.email && (
                  <>
                    {/* Separator */}
                    <Typography variant="body2" sx={{ mx: 1, color: 'text.secondary' }}>
                      |
                    </Typography>

                    {/* From chip */}
                    {event.metadata.email.from_email && (
                      <Chip
                        label={`from: ${event.metadata.email.from_name || event.metadata.email.from_email}`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          backgroundColor: 'rgba(0, 0, 0, 0.06)',
                          '& .MuiChip-label': {
                            px: 1,
                          },
                        }}
                      />
                    )}

                    {/* To chips - show first 2 */}
                    {event.metadata.email.to_emails && event.metadata.email.to_emails.slice(0, 2).map((email: string, idx: number) => (
                      <Chip
                        key={`to-${idx}`}
                        label={`to: ${email}`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          ml: 0.5,
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          color: 'primary.main',
                          '& .MuiChip-label': {
                            px: 1,
                          },
                        }}
                      />
                    ))}

                    {/* +N more chip if there are additional recipients */}
                    {event.metadata.email.to_emails && event.metadata.email.to_emails.length > 2 && (
                      <Chip
                        label={`+${event.metadata.email.to_emails.length - 2} more`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          ml: 0.5,
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          color: 'primary.main',
                          '& .MuiChip-label': {
                            px: 1,
                          },
                        }}
                      />
                    )}

                    {/* Cc chips - show first 2 */}
                    {event.metadata.email.cc_emails && event.metadata.email.cc_emails.slice(0, 2).map((email: string, idx: number) => (
                      <Chip
                        key={`cc-${idx}`}
                        label={`cc: ${email}`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          ml: 0.5,
                          backgroundColor: 'rgba(156, 39, 176, 0.08)',
                          color: 'secondary.main',
                          '& .MuiChip-label': {
                            px: 1,
                          },
                        }}
                      />
                    ))}

                    {/* +N more chip for CC if there are additional recipients */}
                    {event.metadata.email.cc_emails && event.metadata.email.cc_emails.length > 2 && (
                      <Chip
                        label={`+${event.metadata.email.cc_emails.length - 2} more`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          ml: 0.5,
                          backgroundColor: 'rgba(156, 39, 176, 0.08)',
                          color: 'secondary.main',
                          '& .MuiChip-label': {
                            px: 1,
                          },
                        }}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        </Box>

        {/* Eliot Summary button for email events */}
        {EMAIL_EVENTS.includes(event.event_type) && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              email.handleOpenSummaryPopover(e, event.id);
            }}
            sx={{
              color: 'primary.main',
              padding: '4px',
              '&:hover': {
                backgroundColor: 'primary.light',
              },
            }}
            title="Summarize with Eliot"
          >
            <AiIcon fontSize="small" />
          </IconButton>
        )}

        {/* Event type badge */}
        <Chip
          label={getEventCategoryLabel(event.event_type, EMAIL_EVENTS, TASK_EVENTS, DOCUMENT_EVENTS, ENTITY_EVENTS, DEFENDANT_EVENTS, TEAM_EVENTS)}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: EVENT_TYPE_COLORS[event.event_type] || '#757575',
            color: 'white',
            fontWeight: 500,
          }}
        />

        {/* Important badge */}
        {event.is_important && (
          <Chip
            label="Important"
            size="small"
            color="warning"
            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
          />
        )}

        {/* Expand/Collapse icon */}
        <IconButton
          size="small"
          sx={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
          onClick={(e) => {
            e.stopPropagation();
            toggleEventExpanded(event.id);
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Expanded content */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Divider />
        <CardContent sx={{ pt: 2 }}>
          {/* Editing UI - varies by event type */}
          {isEditing && event.event_type === 'log_entry' ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Edit Case Log Entry
              </Typography>

              {/* Date & Time */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                  Date & Time
                </Typography>
                <TextField
                  type="datetime-local"
                  size="small"
                  value={editEventDate}
                  onChange={(e) => setEditEventDate(e.target.value)}
                  sx={{ width: 250 }}
                />
              </Box>

              {/* Content */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                  Content
                </Typography>
                <RichTextEditor
                  initialContent={editLogContent}
                  onChange={(content) => setEditLogContent(content)}
                  placeholder="Enter case log details..."
                  minHeight={150}
                  showSaveButton={false}
                />
              </Box>

              {/* Existing Attachments */}
              {editLogExistingAttachments.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AttachFileIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Existing Attachments ({editLogExistingAttachments.length})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {editLogExistingAttachments.map((attachment) => (
                      <Chip
                        key={attachment.id}
                        label={attachment.original_filename}
                        icon={<AttachFileIcon />}
                        onDelete={() => {
                          setEditLogAttachmentsToRemove([...editLogAttachmentsToRemove, attachment.id]);
                          setEditLogExistingAttachments(editLogExistingAttachments.filter(a => a.id !== attachment.id));
                        }}
                        onClick={async () => {
                          try {
                            const response = await api.get(attachment.file_url, { headers });
                            const downloadUrl = response.data.url;

                            // For local development, fetch with auth and create blob
                            if (downloadUrl.startsWith('http://localhost') || downloadUrl.startsWith('http://127.0.0.1')) {
                              const urlPath = downloadUrl.replace('http://localhost:8000/api', '');
                              const fileResponse = await api.get(urlPath, {
                                headers,
                                responseType: 'blob'
                              });

                              const blob = new Blob([fileResponse.data]);
                              const blobUrl = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = blobUrl;
                              link.download = attachment.original_filename;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(blobUrl);
                            } else {
                              const link = document.createElement('a');
                              link.href = downloadUrl;
                              link.download = attachment.original_filename;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }
                          } catch (error) {
                            console.error('Failed to download attachment:', error);
                            alert('Failed to download attachment. Please try again.');
                          }
                        }}
                        clickable
                        size="small"
                        sx={{
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Add New Attachments */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <UploadIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Add New Attachments
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  size="small"
                >
                  Upload Files
                  <input
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setEditLogFiles([...editLogFiles, ...files]);
                    }}
                    accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
                  />
                </Button>
                {editLogFiles.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      {editLogFiles.length} file{editLogFiles.length !== 1 ? 's' : ''} to upload:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {editLogFiles.map((file, index) => (
                        <Chip
                          key={index}
                          label={file.name}
                          onDelete={() => setEditLogFiles(editLogFiles.filter((_, i) => i !== index))}
                          size="small"
                          icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Action buttons */}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CheckIcon />}
                  onClick={() => handleSaveLogEntry(event)}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : isEditing && TASK_EVENTS.includes(event.event_type) ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Edit Task
              </Typography>

              {/* Title */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                  Title
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={editTaskTitle}
                  onChange={(e) => setEditTaskTitle(e.target.value)}
                  placeholder="Enter task title..."
                />
              </Box>

              {/* Description */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                  Description
                </Typography>
                <RichTextEditor
                  initialContent={editTaskDescription}
                  onChange={(content) => setEditTaskDescription(content)}
                  placeholder="Enter task description..."
                  minHeight={120}
                  showSaveButton={false}
                />
              </Box>

              {/* Status and Priority */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                    Status
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={editTaskStatus}
                      onChange={(e) => setEditTaskStatus(e.target.value)}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                      <MenuItem value="blocked">Blocked</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                    Priority
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={editTaskPriority}
                      onChange={(e) => setEditTaskPriority(e.target.value)}
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Assigned To and Due Date */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                    Assigned To
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={editTaskAssignedTo}
                      onChange={(e) => setEditTaskAssignedTo(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Unassigned</em>
                      </MenuItem>
                      {availableUsers.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.first_name && user.last_name
                            ? `${user.first_name} ${user.last_name}`
                            : user.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                    Due Date
                  </Typography>
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={editTaskDueDate}
                    onChange={(e) => setEditTaskDueDate(e.target.value)}
                  />
                </Box>
              </Box>

              {/* Existing Attachments */}
              {editTaskExistingAttachments.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AttachFileIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Existing Attachments ({editTaskExistingAttachments.length})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {editTaskExistingAttachments.map((attachment) => (
                      <Chip
                        key={attachment.id}
                        label={attachment.original_filename}
                        icon={<AttachFileIcon />}
                        onDelete={() => {
                          setEditTaskAttachmentsToRemove([...editTaskAttachmentsToRemove, attachment.id]);
                          setEditTaskExistingAttachments(editTaskExistingAttachments.filter(a => a.id !== attachment.id));
                        }}
                        onClick={async () => {
                          try {
                            const response = await api.get(attachment.file_url, { headers });
                            const downloadUrl = response.data.url;

                            // For local development, fetch with auth and create blob
                            if (downloadUrl.startsWith('http://localhost') || downloadUrl.startsWith('http://127.0.0.1')) {
                              const urlPath = downloadUrl.replace('http://localhost:8000/api', '');
                              const fileResponse = await api.get(urlPath, {
                                headers,
                                responseType: 'blob'
                              });

                              const blob = new Blob([fileResponse.data]);
                              const blobUrl = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = blobUrl;
                              link.download = attachment.original_filename;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(blobUrl);
                            } else {
                              const link = document.createElement('a');
                              link.href = downloadUrl;
                              link.download = attachment.original_filename;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }
                          } catch (error) {
                            console.error('Failed to download attachment:', error);
                            alert('Failed to download attachment. Please try again.');
                          }
                        }}
                        clickable
                        size="small"
                        sx={{
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Add New Attachments */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <UploadIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Add New Attachments
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  size="small"
                >
                  Upload Files
                  <input
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setEditTaskFiles([...editTaskFiles, ...files]);
                    }}
                    accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
                  />
                </Button>
                {editTaskFiles.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      {editTaskFiles.length} file{editTaskFiles.length !== 1 ? 's' : ''} to upload:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {editTaskFiles.map((file, index) => (
                        <Chip
                          key={index}
                          label={file.name}
                          onDelete={() => setEditTaskFiles(editTaskFiles.filter((_, i) => i !== index))}
                          size="small"
                          icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Action buttons */}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CheckIcon />}
                  onClick={() => handleSaveTask(event)}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : isEditing ? (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Event Date & Time:
                </Typography>
                <TextField
                  type="datetime-local"
                  size="small"
                  value={editEventDate}
                  onChange={(e) => setEditEventDate(e.target.value)}
                  sx={{ width: 220 }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleSaveEventDate(event.id)}
                  color="primary"
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleCancelEdit}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
              {/* Date & Time - only show inline edit for log entries, hide for email events */}
              {!event.event_type.includes('task') && !EMAIL_EVENTS.includes(event.event_type) && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Date & Time:</strong>{' '}
                    {new Date(event.event_date).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </Typography>
                  {/* Only show inline edit pencil for log entries */}
                  {event.event_type === 'log_entry' && (
                    <IconButton
                      size="small"
                      onClick={() => handleEditLogEntry(event)}
                      sx={{
                        ml: 0.5,
                        opacity: 0.6,
                        '&:hover': { opacity: 1 }
                      }}
                    >
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </>
              )}
            </Box>
          )}

          {/* Author / Assigned By - hide for email events since Gmail header shows this */}
          {!isEditing && event.author_name && !EMAIL_EVENTS.includes(event.event_type) && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              <strong>{event.event_type.includes('task') ? 'Assigned By:' : 'Author:'}</strong> {event.author_name}
            </Typography>
          )}

          {/* Description - hide for task events, log entries, and email events since we show structured data below */}
          {!isEditing && event.description && !event.event_type.includes('task') && event.event_type !== 'log_entry' && !EMAIL_EVENTS.includes(event.event_type) && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {event.description}
            </Typography>
          )}

          {/* Metadata details - enhanced for different event types */}
          {!isEditing && event.metadata && Object.keys(event.metadata).length > 0 && (
            <Box sx={{ mb: 2 }}>
              {/* Entity list for entity events */}
              {event.metadata.entities && Array.isArray(event.metadata.entities) && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Entities ({event.metadata.entity_count || event.metadata.entities.length}):
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 1,
                      pl: 2,
                    }}
                  >
                    {event.metadata.entities.map((entity: any, idx: number) => (
                      <Box key={idx}>
                        <Typography variant="body2">
                          {entity.name}
                        </Typography>
                        {entity.identifier && (
                          <Typography variant="caption" color="text.secondary">
                            {entity.identifier}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Defendant list for defendant events */}
              {event.metadata.defendants && Array.isArray(event.metadata.defendants) && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Defendants ({event.metadata.defendant_count || event.metadata.defendants.length}):
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 1,
                      pl: 2,
                    }}
                  >
                    {event.metadata.defendants.map((defendant: any, idx: number) => (
                      <Box key={idx}>
                        <Typography variant="body2">
                          {defendant.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Document preview for document events */}
              {event.event_type.includes('document') && event.metadata.filename && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Filename:</strong> {event.metadata.filename}
                  </Typography>
                  {event.metadata.size_bytes && (
                    <Typography variant="body2">
                      <strong>Size:</strong> {(event.metadata.size_bytes / 1024).toFixed(1)} KB
                    </Typography>
                  )}
                  {event.metadata.is_operative !== undefined && (
                    <Typography variant="body2">
                      <strong>Status:</strong> {event.metadata.is_operative ? 'Operative' : 'Pending Approval'}
                    </Typography>
                  )}
                  {/* Document preview placeholder - will be populated when backend adds preview_url */}
                  {event.metadata.preview_url && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        backgroundColor: 'grey.50',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <ImageIcon color="action" />
                      <Typography variant="caption">
                        Document preview (coming soon)
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Log entry content */}
              {event.event_type === 'log_entry' && event.metadata.content && (
                <Box sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: 'grey.50',
                  borderLeft: '3px solid',
                  borderLeftColor: 'primary.main',
                  borderRadius: 1
                }}>
                  <RichTextDisplay content={event.metadata.content} maxLength={200} />
                </Box>
              )}

              {/* Attachments for log entry events */}
              {event.event_type === 'log_entry' && event.metadata.attachments && event.metadata.attachments.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                    Attachments ({event.metadata.attachments.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {event.metadata.attachments.map((attachment: any) => (
                      <Chip
                        key={attachment.id}
                        label={attachment.original_filename}
                        size="small"
                        icon={<DocumentIcon sx={{ fontSize: 14 }} />}
                        onClick={async () => {
                          try {
                            const response = await api.get(attachment.file_url, { headers });
                            const downloadUrl = response.data.url;

                            // Check if this is a local development URL
                            const isLocalUrl = downloadUrl.includes('localhost') || downloadUrl.includes('127.0.0.1');

                            if (isLocalUrl) {
                              // Local development - fetch as blob with auth headers
                              // Extract the path from the full URL to use with axios
                              const urlObj = new URL(downloadUrl);
                              let relativePath = urlObj.pathname + urlObj.search;

                              // Remove /api/ prefix if present since axios api instance already includes it
                              if (relativePath.startsWith('/api/')) {
                                relativePath = relativePath.substring(5); // Remove '/api/'
                              }

                              const fileResponse = await api.get(relativePath, {
                                headers,
                                responseType: 'blob'
                              });

                              const blob = new Blob([fileResponse.data]);
                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', attachment.original_filename);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                            } else {
                              // Production S3 presigned URL - use directly
                              const link = document.createElement('a');
                              link.href = downloadUrl;
                              link.setAttribute('download', attachment.original_filename);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                                  }
                                } catch (error) {
                            console.error('Failed to download attachment:', error);
                          }
                        }}
                        sx={{
                          cursor: 'pointer',
                          fontSize: '0.7rem',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                          }
                        }}
                        clickable
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Task description for task events */}
              {event.event_type.includes('task') && event.metadata.task_title && (
                <Box sx={{ mb: 2 }}>
                  {/* Task Description */}
                  {event.metadata.task_description && (
                    <Box sx={{
                      mb: 2,
                      p: 2,
                      backgroundColor: 'grey.50',
                      borderLeft: '3px solid',
                      borderLeftColor: 'warning.main',
                      borderRadius: 1
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                        Description
                      </Typography>
                      <RichTextDisplay content={event.metadata.task_description} />
                    </Box>
                  )}

                  {/* Status and Priority Row */}
                  <Box sx={{ display: 'flex', gap: 3, mb: 1.5, flexWrap: 'wrap' }}>
                    {/* Status */}
                    {event.metadata.status && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Status:
                        </Typography>
                        <Chip
                          label={getStatusDisplay(event.metadata.status)}
                          size="small"
                          color={STATUS_COLORS[event.metadata.status as keyof typeof STATUS_COLORS] || 'default'}
                          sx={{ fontWeight: 600, height: 22 }}
                        />
                      </Box>
                    )}

                    {/* Priority */}
                    {event.metadata.priority && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Priority:
                        </Typography>
                        <Chip
                          label={event.metadata.priority.charAt(0).toUpperCase() + event.metadata.priority.slice(1)}
                          size="small"
                          color="default"
                          sx={{ fontWeight: 600, height: 22 }}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Assigned To and Due Date Row */}
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {/* Assigned To */}
                    {event.metadata.assigned_to_name && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Assigned to:
                        </Typography>
                        <Typography variant="body2">
                          {event.metadata.assigned_to_name}
                        </Typography>
                      </Box>
                    )}

                    {/* Due Date */}
                    {event.metadata.due_date && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Due:
                        </Typography>
                        <Typography variant="body2">
                          {new Date(event.metadata.due_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Document Preview (for document approval tasks) */}
                  {event.metadata.document_filename && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                        Document
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {/* Thumbnail */}
                        {event.metadata.thumbnail_url && (
                          <Box
                            component="img"
                            src={event.metadata.thumbnail_url}
                            alt={event.metadata.document_filename}
                            sx={{
                              width: 80,
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.400',
                              flexShrink: 0
                            }}
                            onError={(e: any) => {
                              // Hide image on error
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        {/* Document Info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {event.metadata.document_filename}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {event.metadata.document_web_url && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DocumentIcon />}
                                onClick={() => {
                                  // Convert SharePoint URL to Office protocol format (ms-word:ofe|u|...)
                                  const filename = event.metadata.document_filename || '';
                                  const ext = filename.split('.').pop()?.toLowerCase();

                                  // Map file extensions to Office protocol handlers
                                  const protocolMap: { [key: string]: string } = {
                                    'doc': 'ms-word',
                                    'docx': 'ms-word',
                                    'docm': 'ms-word',
                                    'xls': 'ms-excel',
                                    'xlsx': 'ms-excel',
                                    'xlsm': 'ms-excel',
                                    'ppt': 'ms-powerpoint',
                                    'pptx': 'ms-powerpoint',
                                    'pptm': 'ms-powerpoint'
                                  };

                                  const protocol = protocolMap[ext || ''] || 'ms-word';
                                  const officeUrl = `${protocol}:ofe|u|${event.metadata.document_web_url}`;

                                  // Use window.location.href to trigger Office protocol handler
                                  window.location.href = officeUrl;
                                }}
                                sx={{ fontSize: '0.75rem', py: 0.25 }}
                              >
                                Open
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* Task Attachments */}
                  {event.metadata.attachments && event.metadata.attachments.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                        Attachments ({event.metadata.attachments.length}):
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {event.metadata.attachments.map((attachment: any) => (
                          <Chip
                            key={attachment.id}
                            label={attachment.original_filename}
                            size="small"
                            icon={<DocumentIcon sx={{ fontSize: 14 }} />}
                            onClick={async () => {
                              try {
                                const response = await api.get(attachment.file_url, { headers });
                                const downloadUrl = response.data.url;

                                // Check if this is a local development URL
                                const isLocalUrl = downloadUrl.includes('localhost') || downloadUrl.includes('127.0.0.1');

                                if (isLocalUrl) {
                                  // Local development - fetch as blob with auth headers
                                  const urlObj = new URL(downloadUrl);
                                  let relativePath = urlObj.pathname + urlObj.search;

                                  // Remove /api/ prefix if present
                                  if (relativePath.startsWith('/api/')) {
                                    relativePath = relativePath.substring(5);
                                  }

                                  const fileResponse = await api.get(relativePath, {
                                    headers,
                                    responseType: 'blob'
                                  });

                                  const blob = new Blob([fileResponse.data]);
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', attachment.original_filename);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  window.URL.revokeObjectURL(url);
                                } else {
                                  // Production S3 presigned URL - use directly
                                  const link = document.createElement('a');
                                  link.href = downloadUrl;
                                  link.setAttribute('download', attachment.original_filename);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  }
                                } catch (error) {
                                console.error('Failed to download attachment:', error);
                              }
                            }}
                            sx={{
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'primary.contrastText',
                              }
                            }}
                            clickable
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Email event details */}
              {EMAIL_EVENTS.includes(event.event_type) && event.metadata.email && (
                <Box sx={{ mb: 2 }}>
                  {/* Gmail-Style Email Header */}
                  <Box sx={{ mb: 2 }}>
                    {/* Main header row with sender and date */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                      {/* Sender avatar circle with initial */}
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          color: 'white',
                          fontSize: '1.1rem',
                          fontWeight: 600,
                        }}
                      >
                        {(event.metadata.email.from_name || event.metadata.email.from_email || 'U')[0].toUpperCase()}
                      </Box>

                      {/* Sender info and date */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {event.metadata.email.from_name || event.metadata.email.from_email}
                            </Typography>
                            {event.metadata.email.from_name && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                {`<${event.metadata.email.from_email}>`}
                              </Typography>
                            )}
                          </Box>
                          {/* Date on the right */}
                          {event.metadata.email.sent_date && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                              {new Date(event.metadata.email.sent_date).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </Typography>
                          )}
                        </Box>

                        {/* To line with expandable details */}
                        <Box sx={{ mt: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              cursor: 'pointer',
                              '&:hover': { color: 'text.primary' },
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                            onClick={() => {
                              setExpandedEmailDetails(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(event.id)) {
                                  newSet.delete(event.id);
                                } else {
                                  newSet.add(event.id);
                                }
                                return newSet;
                              });
                            }}
                          >
                            to {event.metadata.email.to_emails && event.metadata.email.to_emails.length > 0
                              ? event.metadata.email.to_emails.length === 1
                                ? event.metadata.email.to_emails[0].split('@')[0]
                                : `${event.metadata.email.to_emails.length} recipients`
                              : 'me'}
                            {expandedEmailDetails.has(event.id) ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
                          </Typography>
                        </Box>

                        {/* Expanded details */}
                        <Collapse in={expandedEmailDetails.has(event.id)}>
                          <Box sx={{ mt: 1, pl: 0, fontSize: '0.75rem' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              <strong>from:</strong> {event.metadata.email.from_name ? `${event.metadata.email.from_name} <${event.metadata.email.from_email}>` : event.metadata.email.from_email}
                            </Typography>
                            {event.metadata.email.to_emails && event.metadata.email.to_emails.length > 0 && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                <strong>to:</strong> {event.metadata.email.to_emails.join(', ')}
                              </Typography>
                            )}
                            {event.metadata.email.cc_emails && event.metadata.email.cc_emails.length > 0 && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                <strong>cc:</strong> {event.metadata.email.cc_emails.join(', ')}
                              </Typography>
                            )}
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              <strong>date:</strong> {new Date(event.metadata.email.sent_date).toLocaleString('en-US', {
                                weekday: 'short',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              <strong>subject:</strong> {event.metadata.email.subject}
                            </Typography>
                          </Box>
                        </Collapse>
                      </Box>
                    </Box>
                  </Box>

                  {/* Email Body - Parse Thread with HTML Support */}
                  {(event.metadata.email.body_html || event.metadata.email.body_plain) && (() => {
                    // Prefer HTML body for rich formatting, fall back to plain text
                    const hasHtml = !!event.metadata.email.body_html;
                    const body = hasHtml ? event.metadata.email.body_html : event.metadata.email.body_plain;

                    interface ThreadMessage {
                      content: string;
                      sender?: string;
                      date?: string;
                      isLatest: boolean;
                    }

                    const messages: ThreadMessage[] = [];

                    if (hasHtml) {
                      // Parse HTML email - look for Gmail quote divs or blockquotes
                      // Sanitize first to prevent browser from trying to load cid: references
                      const preSanitized = sanitizeEmailHtml(body);
                      const tempDiv = document.createElement('div');
                      tempDiv.innerHTML = preSanitized;

                      // Try to find Gmail-style quoted content (only direct children, not nested)
                      const allQuotes = Array.from(tempDiv.querySelectorAll('.gmail_quote'));

                      // Filter to only top-level quotes (not inside another .gmail_quote)
                      const gmailQuotes = allQuotes.filter(quote => {
                        let parent = quote.parentElement;
                        while (parent && parent !== tempDiv) {
                          if (parent.classList.contains('gmail_quote')) {
                            return false; // This is a nested quote, skip it
                          }
                          parent = parent.parentElement;
                        }
                        return true; // This is a top-level quote
                      });

                      if (gmailQuotes.length > 0) {
                        // Gmail format detected
                        // The latest message is everything before the first .gmail_quote
                        const firstQuote = gmailQuotes[0];
                        const latestContent = [];

                        // Get all nodes before first quote
                        let node = tempDiv.firstChild;
                        while (node && node !== firstQuote) {
                          latestContent.push(node);
                          node = node.nextSibling;
                        }

                        if (latestContent.length > 0) {
                          const latestDiv = document.createElement('div');
                          latestContent.forEach(n => latestDiv.appendChild(n.cloneNode(true)));

                          // Remove any nested gmail_quote divs from the latest content to avoid duplication
                          // (this handles cases where gmail_quote is nested inside a wrapper div)
                          const nestedQuotes = latestDiv.querySelectorAll('.gmail_quote');
                          nestedQuotes.forEach(q => q.remove());

                          // Use our sanitization function to remove cid: references
                          const sanitized = sanitizeEmailHtml(latestDiv.innerHTML);
                          messages.push({
                            content: sanitized,
                            isLatest: true
                          });
                        }

                        // Process each top-level quoted section only
                        gmailQuotes.forEach((quoteDiv) => {
                          // Try to extract sender/date from the "On ... wrote:" line
                          const quoteText = quoteDiv.textContent || '';
                          const headerMatch = quoteText.match(/On (.+?) at (.+?) (.+?) <(.+?)> wrote:/);

                          let sender = 'Previous Message';
                          let date = '';

                          if (headerMatch) {
                            sender = `${headerMatch[3]} <${headerMatch[4]}>`;
                            date = `${headerMatch[1]} at ${headerMatch[2]}`;
                          }

                          // Use our sanitization function to remove cid: references
                          const sanitized = sanitizeEmailHtml(quoteDiv.innerHTML);
                          messages.push({
                            content: sanitized,
                            sender,
                            date,
                            isLatest: false
                          });
                        });
                      } else {
                        // No Gmail quotes found, just render the whole HTML
                        // Use our sanitization function to remove cid: references
                        const sanitized = sanitizeEmailHtml(body);
                        messages.push({
                          content: sanitized,
                          isLatest: true
                        });
                      }
                    } else {
                      // Plain text parsing (existing logic)
                      const quotedHeaderPattern = /^On (.+?) at (.+?) (.+?) <(.+?)> wrote:/gm;
                      const matches: Array<{ sender: string; date: string; index: number }> = [];
                      let match;

                      while ((match = quotedHeaderPattern.exec(body)) !== null) {
                        matches.push({
                          sender: `${match[3]} <${match[4]}>`,
                          date: `${match[1]} at ${match[2]}`,
                          index: match.index
                        });
                      }

                      if (matches.length === 0) {
                        messages.push({
                          content: body.trim(),
                          isLatest: true
                        });
                      } else {
                        const latestContent = body.substring(0, matches[0].index).trim();
                        if (latestContent) {
                          messages.push({
                            content: latestContent,
                            isLatest: true
                          });
                        }

                        for (let i = 0; i < matches.length; i++) {
                          const startIdx = matches[i].index;
                          const endIdx = i < matches.length - 1 ? matches[i + 1].index : body.length;
                          const headerEndIdx = body.indexOf('\n', startIdx);
                          const messageContent = body.substring(headerEndIdx + 1, endIdx).trim();

                          messages.push({
                            content: messageContent,
                            sender: matches[i].sender,
                            date: matches[i].date,
                            isLatest: false
                          });
                        }
                      }
                    }

                    // Debug logging
                    console.log('Email rendering debug:', {
                      messageCount: messages.length,
                      hasHtml,
                      messages: messages.map((m, i) => ({
                        index: i,
                        isLatest: m.isLatest,
                        contentPreview: m.content.substring(0, 100),
                        sender: m.sender
                      }))
                    });

                    return (
                      <Box sx={{ mb: 2, maxHeight: '600px', overflowY: 'auto' }}>
                        {messages.map((msg, idx) => (
                          <Box key={idx} sx={{ mb: 2 }}>
                            {hasHtml ? (
                              <Box
                                sx={{
                                  '& table': { borderCollapse: 'collapse', width: '100%', mb: 2 },
                                  '& td, & th': { border: '1px solid #ddd', padding: '8px' },
                                  '& th': { backgroundColor: '#f5f5f5', fontWeight: 600 },
                                  '& p': { margin: '0.5em 0' },
                                  '& a': { color: 'primary.main' },
                                  fontSize: '0.875rem',
                                  wordBreak: 'break-word',
                                  '& img': { maxWidth: '100%', height: 'auto' },
                                  lineHeight: 1.6
                                }}
                                dangerouslySetInnerHTML={{ __html: msg.content }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
                                {msg.content}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    );
                  })()}

                  {/* Attachments */}
                  {event.metadata.email.has_attachments && event.metadata.email.attachments && event.metadata.email.attachments.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                        Attachments ({event.metadata.email.attachments.length}):
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {event.metadata.email.attachments.map((attachment: any, idx: number) => (
                          <Chip
                            key={idx}
                            label={`${attachment.filename} (${(attachment.size / 1024).toFixed(1)} KB)`}
                            size="small"
                            icon={<DocumentIcon sx={{ fontSize: 14 }} />}
                            onClick={async () => {
                              try {
                                // Build the file_url endpoint for email attachments
                                const fileUrlEndpoint = `/cases/${caseId}/emails/${event.metadata.email.message_id}/attachments/${idx}/file_url/`;
                                const response = await api.get(fileUrlEndpoint, { headers });
                                const downloadUrl = response.data.url;

                                // Check if this is a local development URL
                                const isLocalUrl = downloadUrl.includes('localhost') || downloadUrl.includes('127.0.0.1');

                                if (isLocalUrl) {
                                  // Local development - fetch as blob with auth headers
                                  const urlObj = new URL(downloadUrl);
                                  let relativePath = urlObj.pathname + urlObj.search;

                                  // Remove /api/ prefix if present
                                  if (relativePath.startsWith('/api/')) {
                                    relativePath = relativePath.substring(5);
                                  }

                                  const fileResponse = await api.get(relativePath, {
                                    headers,
                                    responseType: 'blob'
                                  });

                                  // Create blob URL and trigger download
                                  const blob = new Blob([fileResponse.data]);
                                  const blobUrl = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = blobUrl;
                                  link.download = attachment.filename;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(blobUrl);
                                } else {
                                  // Production S3 URL - direct download
                                  const link = document.createElement('a');
                                  link.href = downloadUrl;
                                  link.download = attachment.filename;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }
                              } catch (error) {
                                console.error('Failed to download email attachment:', error);
                              }
                            }}
                            sx={{
                              fontSize: '0.7rem',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'white',
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Generic metadata display - hide for task events and email events */}
              {!event.event_type.includes('task') && !EMAIL_EVENTS.includes(event.event_type) && formatMetadata(event) && (
                <Typography variant="body2" color="text.secondary">
                  {formatMetadata(event)}
                </Typography>
              )}
            </Box>
          )}

          {/* Actions */}
          {!isEditing && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              {(event.event_type === 'log_entry' || TASK_EVENTS.includes(event.event_type)) && (
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    if (event.event_type === 'log_entry' && event.related_object_id) {
                      handleEditLogEntry(event);
                    } else if (TASK_EVENTS.includes(event.event_type) && event.related_object_id) {
                      handleEditTask(event);
                    } else {
                      handleEditEvent(event);
                    }
                  }}
                  variant="outlined"
                >
                  Edit
                </Button>
              )}
              {event.event_type !== 'log_entry' && !TASK_EVENTS.includes(event.event_type) && (
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEditEvent(event)}
                  variant="outlined"
                >
                  Edit
                </Button>
              )}
              <Button
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => handleDeleteClick(event)}
                color="error"
                variant="outlined"
              >
                Delete
              </Button>
            </Box>
          )}
        </CardContent>
      </Collapse>
        </Card>
      </Box>
    </Box>
  );
};
